/**
 * Dedicated full My Runs list cache (separate pageCache key from `myRides`).
 * Mirrors the slim run list whenever My Rides cache is written so lists hydrate from IDB/localStorage
 * without falling back to Home’s five “recent rides” cards.
 */

import { cacheKey, setCached, getCachedStale, getCachedStaleAsync } from './pageCache';

function slimRunForListCache(run) {
  if (!run || typeof run !== 'object') return run;
  const tel = run.telemetryData;
  const n = Array.isArray(tel) ? tel.length : 0;
  const { telemetryData, ...rest } = run;
  return {
    ...rest,
    telemetryData: [],
    hasTelemetryData: n > 0,
  };
}

export function myRunsFullListCacheKey(userId) {
  return cacheKey('myRunsFullList', userId);
}

export function persistMyRunsFullList(userId, runs, groups) {
  if (!userId) return;
  const slimRuns = Array.isArray(runs) ? runs.map(slimRunForListCache) : [];
  const groupsSafe = Array.isArray(groups) ? groups : [];
  setCached(myRunsFullListCacheKey(userId), { runs: slimRuns, groups: groupsSafe });
}

export function clearMyRunsFullList(userId) {
  if (!userId) return;
  setCached(myRunsFullListCacheKey(userId), {});
}

/** Writes My Rides page cache and mirrors the full list key. */
export function setMyRidesCacheWithFullList(userId, payload) {
  if (!userId) return;
  const key = cacheKey('myRides', userId);
  setCached(key, payload);
  const runs = payload && Array.isArray(payload.runs) ? payload.runs : [];
  const groups = payload && Array.isArray(payload.groups) ? payload.groups : [];
  persistMyRunsFullList(userId, runs, groups);
}

export function clearMyRidesCaches(userId) {
  if (!userId) return;
  setCached(cacheKey('myRides', userId), {});
  clearMyRunsFullList(userId);
}

/**
 * Stale-ok: sync localStorage first, then IndexedDB.
 * @returns {Promise<{ runs: object[], groups: object[] }|null>}
 */
export async function loadMyRunsFullListStaleAsync(userId) {
  if (!userId) return null;
  const k = myRunsFullListCacheKey(userId);
  const ls = getCachedStale(k);
  if (ls && Array.isArray(ls.runs) && ls.runs.length > 0) return ls;
  const idb = await getCachedStaleAsync(k);
  if (idb && Array.isArray(idb.runs) && idb.runs.length > 0) return idb;
  return null;
}
